import { useMemo } from 'react';

import { compareISO, toISODate } from '@backrs_ui/common-ui/src/utils/dateTime.util';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { useAuth } from '@backrs_ui/grow-ui/src/hooks/useAuth';
import { showSnackbar } from '@backrs_ui/grow-ui/src/redux/appState/appState';
import {
  FEATURE_FLAGS,
  selectIsFeatureFlagEnabled,
} from '@backrs_ui/grow-ui/src/redux/featureFlags/featureFlags';
import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  useGetMostRecentPlanQuery,
  useGetQuestionsForDayQuery,
  useSetDayCompletionMutation,
} from '@backrs_ui/grow-ui/src/redux/satActBooster/satActBooster.api';
import {
  selectBoosterQuizProgress,
  selectBoosterSelectedDay,
  setBoosterSelectedDay,
} from '@backrs_ui/grow-ui/src/redux/satActBooster/satActBooster.slice';
import { PlanDay } from '@backrs_ui/grow-ui/src/redux/satActBooster/satActBooster.types';

import { Box, CircularProgress, SxProps, Theme, Typography } from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/query';

import { BoosterInlineError } from './BoosterInlineError';
import { DayHeader } from './dailyPractice/DayHeader';
import { PracticeQuiz } from './dailyPractice/PracticeQuiz';
import { ResourceCard } from './dailyPractice/ResourceCard';
import { StudyTargets } from './dailyPractice/StudyTargets';
import { useDailyPracticeAgentContext } from './DailyPracticePanel.agentContext';
import { resourceForDay } from '../utils/dailyResource';

const STRINGS = appStrings.modules.satActBooster.dailyPractice;

/** The reporting category a day's quiz draws from; null when the day can't carry one. */
const quizCategoryOf = (day: PlanDay | null): number | null =>
  day?.kind === 'study' ? (day.targets[0]?.scoreCategoryId ?? null) : null;

/**
 * Daily Practice tab body: the plan day selected on the output-page calendar. Renders the day
 * header, study targets, resource card, and (on study days) the practice quiz. Owns the day's
 * question fetch, so the completion toggle can tell a day that owes a quiz from one that never had
 * one. The selected date lives in the store; the tab title never changes.
 */
export const DailyPracticePanel = () => {
  const dispatch = useAppDispatch();
  const { isUserOrGuest } = useAuth();
  const selectedDate = useAppSelector(selectBoosterSelectedDay);
  // The tab lives in the shell, outside the booster routes, so it carries its own flag check.
  const isTestBoosterEnabled = useAppSelector(
    selectIsFeatureFlagEnabled(FEATURE_FLAGS.TEST_BOOSTER),
  );

  const {
    data: mostRecent,
    isLoading,
    isError,
    error,
    refetch,
  } = useGetMostRecentPlanQuery(undefined, {
    skip: !isUserOrGuest || !isTestBoosterEnabled,
  });
  // A 404 is "no plan yet", which the empty state already covers; anything else failed. A read
  // that never yielded data leaves nothing to render; a failed refetch keeps the last plan.
  const loadFailed =
    isError && mostRecent === undefined && (error as { status?: number })?.status !== 404;
  const [setDayCompletion, { isLoading: isSavingCompletion }] = useSetDayCompletionMutation();

  const plan = mostRecent?.plan ?? null;
  const test = mostRecent?.reportFacts.test ?? null;
  const submissionId = mostRecent?.id ?? null;

  const days = useMemo(
    () => (plan ? [...plan.days].sort((a, b) => compareISO(a.date, b.date)) : []),
    [plan],
  );

  const selectedIndex = selectedDate ? days.findIndex((day) => day.date === selectedDate) : -1;
  const day = selectedIndex >= 0 ? days[selectedIndex] : null;

  const resource = useMemo(() => (day && test ? resourceForDay(day, test) : null), [day, test]);

  useDailyPracticeAgentContext(day, resource);

  // `currentData`, not `data`: `data` re-uses the previous day's questions mid-switch, which would
  // render one day's quiz under another day's date.
  const quizCategoryId = quizCategoryOf(day);
  const {
    currentData: questions = [],
    isFetching: isQuestionsFetching,
    isError: isQuestionsError,
    refetch: refetchQuestions,
  } = useGetQuestionsForDayQuery(
    submissionId && day && quizCategoryId !== null
      ? { submissionId, dayDate: day.date }
      : skipToken,
  );

  // A live quiz finished this session shows its results before the day's attempt is refetched.
  const quizProgress = useAppSelector(selectBoosterQuizProgress(day?.date ?? ''));
  // Pending while fetching, since "no questions yet" isn't "no quiz" — but never on a failed load,
  // which would strand a day nobody can prove has one.
  const quizPending =
    quizCategoryId !== null &&
    (isQuestionsFetching || questions.length > 0) &&
    day?.attempt == null &&
    !quizProgress?.showResults;

  // Read off the plan rather than probing each day's bank; an empty bank surfaces on arrival.
  const nextQuizDate = useMemo((): string | null => {
    if (selectedIndex < 0) return null;

    return (
      days
        .slice(selectedIndex + 1)
        .find((planDay) => quizCategoryOf(planDay) !== null && planDay.attempt === null)?.date ??
      null
    );
  }, [days, selectedIndex]);

  const goToToday = () => {
    if (days.length === 0) return;

    const today = toISODate(new Date());
    const exact = days.find((planDay) => planDay.date === today);
    const upcoming = days.find((planDay) => compareISO(planDay.date, today) >= 0);
    const target = exact ?? upcoming ?? days[days.length - 1];

    dispatch(setBoosterSelectedDay(target.date));
  };

  // Loading and failure are both distinguished from "no day selected" — otherwise a cold open
  // or a dropped request reads as the student not having picked a day.
  if (isLoading) {
    return (
      <Box sx={emptyRootSx} id="booster-daily-practice-panel">
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (loadFailed) {
    return (
      <Box sx={emptyRootSx} id="booster-daily-practice-panel">
        <BoosterInlineError id="booster-daily-practice-error" onRetry={refetch} />
      </Box>
    );
  }

  if (!day || !test || !submissionId) {
    return (
      <Box sx={emptyRootSx} id="booster-daily-practice-panel">
        <Typography variant="text-sm-regular" sx={mutedSx}>
          {STRINGS.noDaySelected}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={rootSx} id="booster-daily-practice-panel">
      <DayHeader
        day={day}
        canPrev={selectedIndex > 0}
        canNext={selectedIndex < days.length - 1}
        onPrev={() => dispatch(setBoosterSelectedDay(days[selectedIndex - 1].date))}
        onNext={() => dispatch(setBoosterSelectedDay(days[selectedIndex + 1].date))}
        onToday={goToToday}
        onToggleComplete={() => {
          setDayCompletion({ submissionId, dayDate: day.date, completed: !day.completed })
            .unwrap()
            .catch(() =>
              dispatch(showSnackbar({ message: STRINGS.completionError, severity: 'error' })),
            );
        }}
        toggleDisabled={isSavingCompletion}
        // Only marking complete is gated; a completed day can always be undone.
        toggleBlockedReason={
          quizPending && !day.completed ? STRINGS.header.quizRequired : undefined
        }
      />

      <Box sx={contentSx}>
        <Box sx={detailRowSx}>
          <Box sx={targetsColSx}>
            <StudyTargets day={day} resource={resource} />
          </Box>
          {resource && (
            <Box sx={resourceColSx}>
              <ResourceCard resource={resource} />
            </Box>
          )}
        </Box>

        {day.kind === 'study' && day.targets.length > 0 && (
          <PracticeQuiz
            key={day.date}
            day={day}
            submissionId={submissionId}
            questions={questions}
            isQuestionsFetching={isQuestionsFetching}
            isQuestionsError={isQuestionsError}
            onRetryQuestions={refetchQuestions}
            onNextQuiz={
              nextQuizDate ? () => dispatch(setBoosterSelectedDay(nextQuizDate)) : undefined
            }
          />
        )}
      </Box>
    </Box>
  );
};

const rootSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  paddingBottom: backrs.spacing['spacing-4xl'],
});

// A size container so the two-column rows below flip on the panel's own width rather than the
// viewport — the panel shares the screen with the Lubav sidebar, so viewport width overstates it.
const contentSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-4xl'],
  paddingTop: backrs.spacing['spacing-3xl'],
  containerType: 'inline-size',
});

const emptyRootSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: backrs.spacing['spacing-3xl'],
});

const mutedSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const detailRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-2xl'],
  '@container (min-width: 720px)': {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
});

const targetsColSx: SxProps<Theme> = {
  width: '100%',
  minWidth: 0,
  '@container (min-width: 720px)': { flex: '1 1 0' },
};

const resourceColSx: SxProps<Theme> = {
  width: '100%',
  '@container (min-width: 720px)': { flex: '0 1 360px', maxWidth: '360px' },
};
